import { useEffect, useState } from 'react';
import { useWLOrgConfigContext } from '../../../providers/WLOrgConfigProvider';
import { OrderFormSides } from '../../../types';
import { POST, request } from '../../../utils/http';
import { logger } from '../../../utils/logger';

export interface MaxOrderAmountResponse {
  MaxOrderAmount: string;
  Currency: string;
  FeesIncluded: boolean;
}

export interface MaxOrderAmountProps {
  MarketAccount: string | undefined;
  Symbol: string | undefined;
  Currency: string | undefined;
  Side: string | undefined;
  Price?: string;
}

export const useMaxOrderQuantity = (maxAmountProps: MaxOrderAmountProps) => {
  const [maxOrderAmountResponse, setMaxOrderAmountResponse] = useState<MaxOrderAmountResponse>();
  const { config } = useWLOrgConfigContext();

  const allowedToRequest = config.useBackendMaxAvailable === true;
  const [requesting, setRequesting] = useState(allowedToRequest);

  useEffect(() => {
    // Check if the feature flag (if any) is enabled
    if (!allowedToRequest) {
      setMaxOrderAmountResponse(undefined);
      return;
    }

    const requiredFields = [
      'MarketAccount',
      'Symbol',
      'Currency',
      'Side',
    ] as const satisfies (keyof MaxOrderAmountProps)[];

    // If any required fields are missing, don't make the request
    if (requiredFields.some(field => !maxAmountProps[field]) || maxAmountProps.Side === OrderFormSides.Twoway) {
      setMaxOrderAmountResponse(undefined);
      return;
    }

    setRequesting(true);
    request<{ data: MaxOrderAmountResponse[] }>(POST, '/v1/max-order-amount', maxAmountProps)
      .then(response => {
        const orderAmountResponse = response.data[0];
        if (typeof orderAmountResponse.MaxOrderAmount !== 'string') {
          setMaxOrderAmountResponse(undefined);
          return;
        }
        setMaxOrderAmountResponse(orderAmountResponse);
      })
      .catch((err: unknown) => {
        logger.warn('Failed to fetch max order amount', { ...maxAmountProps, err });
        setMaxOrderAmountResponse(undefined);
      })
      .finally(() => {
        setRequesting(false);
      });
  }, [allowedToRequest, maxAmountProps]);

  return { maxOrderAmountResponse, loaded: !requesting };
};
